/**
 * Helper functions to get localized Tarot and IChing card meanings
 * based on the user's selected language
 */

import { tarotMeanings, CardMeaning } from '../assets/tarot_meanings';
import { ichingMeanings, IChingMeaning } from '../assets/iching_meanings';

export type Language = 'en' | 'zh';

// Get current language from localStorage
export const getCurrentLanguage = (): Language => {
    const saved = localStorage.getItem('appLanguage');
    return (saved === 'en' || saved === 'zh') ? saved : 'en';
};

// Helper to get localized string (returns English version if available, falls back to Chinese)
const getLocalizedString = (
    zhValue: string | undefined,
    enValue: string | undefined,
    language: Language
): string => {
    if (language === 'en' && enValue) {
        return enValue;
    }
    return zhValue || enValue || '';
};

// Helper to get localized array (returns English version if available, falls back to Chinese)
const getLocalizedArray = (
    zhValue: string[] | undefined,
    enValue: string[] | undefined,
    language: Language
): string[] => {
    if (language === 'en' && enValue && enValue.length > 0) {
        return enValue;
    }
    return zhValue || enValue || [];
};

// Interface for localized scene content
interface LocalizedScenes {
    career: string;
    relationship: string;
    wealth: string;
    health: string;
    universal: string;
    transition: string;
}

// Helper to get localized scenes
const getLocalizedScenes = (
    zhScenes: LocalizedScenes | undefined,
    enScenes: LocalizedScenes | undefined,
    language: Language
): LocalizedScenes => {
    if (language === 'en' && enScenes) {
        return enScenes;
    }
    return zhScenes || enScenes || {
        career: '',
        relationship: '',
        wealth: '',
        health: '',
        universal: '',
        transition: ''
    };
};

// Interface for localized interpretation
export interface LocalizedInterpretation {
    keywords: string[];
    imageDescription: string;
    generalMeaning: string;
    scenes: LocalizedScenes;
}

// Interface for localized Tarot card
export interface LocalizedTarotCard {
    id: string;
    name: string;
    nameCn: string;
    introduction: string;
    upright: LocalizedInterpretation;
    reversed: LocalizedInterpretation;
}

// Interface for localized IChing hexagram
export interface LocalizedIChingHexagram {
    id: string;
    name: string;
    nameCn: string;
    symbol: string;
    trigrams: { upper: string; lower: string };
    introduction: string;
    upright: LocalizedInterpretation;
    reversed: LocalizedInterpretation;
}

/**
 * Get localized Tarot card meaning by card ID
 * @param cardId - Card ID (e.g., '001', '002', etc.)
 * @param language - Target language ('en' or 'zh')
 * @returns Localized card meaning
 */
export const getLocalizedTarotMeaning = (
    cardId: string,
    language?: Language
): LocalizedTarotCard | null => {
    const lang = language || getCurrentLanguage();
    const card = tarotMeanings[cardId];

    if (!card) return null;

    return {
        id: card.id,
        name: lang === 'en' ? card.name : card.nameCn,
        nameCn: card.nameCn,
        introduction: getLocalizedString(card.introduction, card.introductionEn, lang),
        upright: {
            keywords: getLocalizedArray(card.upright.keywords, card.upright.keywordsEn, lang),
            imageDescription: getLocalizedString(card.upright.imageDescription, card.upright.imageDescriptionEn, lang),
            generalMeaning: getLocalizedString(card.upright.generalMeaning, card.upright.generalMeaningEn, lang),
            scenes: getLocalizedScenes(card.upright.scenes, card.upright.scenesEn, lang)
        },
        reversed: {
            keywords: getLocalizedArray(card.reversed.keywords, card.reversed.keywordsEn, lang),
            imageDescription: getLocalizedString(card.reversed.imageDescription, card.reversed.imageDescriptionEn, lang),
            generalMeaning: getLocalizedString(card.reversed.generalMeaning, card.reversed.generalMeaningEn, lang),
            scenes: getLocalizedScenes(card.reversed.scenes, card.reversed.scenesEn, lang)
        }
    };
};

/**
 * Get localized IChing hexagram meaning by hexagram ID
 * @param hexagramId - Hexagram ID (e.g., '001', '002', etc.)
 * @param language - Target language ('en' or 'zh')
 * @returns Localized hexagram meaning
 */
export const getLocalizedIChingMeaning = (
    hexagramId: string,
    language?: Language
): LocalizedIChingHexagram | null => {
    const lang = language || getCurrentLanguage();
    const hexagram = ichingMeanings[hexagramId];

    if (!hexagram) return null;

    return {
        id: hexagram.id,
        name: lang === 'en' ? hexagram.name : hexagram.nameCn,
        nameCn: hexagram.nameCn,
        symbol: hexagram.symbol,
        trigrams: hexagram.trigrams,
        introduction: getLocalizedString(hexagram.introduction, hexagram.introductionEn, lang),
        upright: {
            keywords: getLocalizedArray(hexagram.upright.keywords, hexagram.upright.keywordsEn, lang),
            imageDescription: getLocalizedString(hexagram.upright.imageDescription, hexagram.upright.imageDescriptionEn, lang),
            generalMeaning: getLocalizedString(hexagram.upright.generalMeaning, hexagram.upright.generalMeaningEn, lang),
            scenes: getLocalizedScenes(hexagram.upright.scenes, hexagram.upright.scenesEn, lang)
        },
        reversed: {
            keywords: getLocalizedArray(hexagram.reversed.keywords, hexagram.reversed.keywordsEn, lang),
            imageDescription: getLocalizedString(hexagram.reversed.imageDescription, hexagram.reversed.imageDescriptionEn, lang),
            generalMeaning: getLocalizedString(hexagram.reversed.generalMeaning, hexagram.reversed.generalMeaningEn, lang),
            scenes: getLocalizedScenes(hexagram.reversed.scenes, hexagram.reversed.scenesEn, lang)
        }
    };
};

/**
 * Get display name for a card
 * @param card - Card meaning object
 * @param language - Target language
 * @returns Display name based on language
 */
export const getCardDisplayName = (
    card: CardMeaning | IChingMeaning,
    language?: Language
): string => {
    const lang = language || getCurrentLanguage();
    return lang === 'en' ? card.name : card.nameCn;
};

/**
 * React hook to get localized meanings (for use in components)
 * Usage: 
 *   import { useLanguage } from '../contexts/LanguageContext';
 *   const { language } = useLanguage();
 *   const localizedCard = getLocalizedTarotMeaning(cardId, language);
 */
